<template>
  <div class="confirmation-container d-flex align-items-center justify-content-center">
    <div class="confirmation-card rounded-4 shadow-lg" ref="cardRef">
      <form @submit.prevent="handleSubmit" class="confirmation-form">
        <!-- Invitado (OCULTO, pero se envía al backend) -->
        <div class="form-group" style="display:none">
          <label class="form-label">Invitado</label>
          <div class="guest-summary" :class="{ 'loading': identityLoading }">
            <template v-if="identityLoading">Cargando invitado...</template>
            <template v-else>
              <div class="guest-name">{{ guestFullName || 'Invitado' }}</div>
            </template>
          </div>
        </div>

        <!-- Título -->
        <div class="form-header anim-el">
          <h2 class="form-title">
            Confirma<br><span class="subtitulo">Asistencia</span>
          </h2>
        </div>

        <!-- Asistencia (radios en columna) -->
        <div class="form-group anim-el">
          <label class="form-label">¿Asistirás a nuestra Boda?</label>
          <div class="radio-group column">
            <label class="radio-option soft-tile">
              <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
              <span class="radio-label">Sí ¡No me la pierdo!</span>
            </label>

            <label class="radio-option soft-tile">
              <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
              <span class="radio-label">No podré asistir, ¡Lo siento!.</span>
            </label>
          </div>
        </div>

        <!-- ¿Cuántos asisten? (visible por defecto; se oculta solo si eligen "no") -->
        <div class="form-group anim-el" v-if="attending !== 'no'">
          <label for="guests" class="form-label">¿Cuántos asisten?</label>
          <div class="select-wrap soft-tile">
            <select
              id="guests"
              v-model="guests"
              class="form-select"
              :disabled="loading"
              :required="attending === 'si'"
            >
              <option value="" disabled>Selecciona una opción</option>
              <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
          <div v-if="loading" class="loading-indicator">Cargando opciones...</div>
        </div>

        <!-- Botón -->
        <div class="actions anim-el">
          <button
            type="submit"
            class="submit-button"
            :disabled="isSubmitting || identityLoading"
          >
            <span v-if="!isSubmitting">Confirmar</span>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </button>
        </div>

        <div v-if="errorMessage" class="alert alert-danger anim-el">{{ errorMessage }}</div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '../api'
import Swal from 'sweetalert2'
import gsap from 'gsap'

export default {
  name: 'ConfirmacionAsistencia',
  props: {
    idInvitado:  { type: Number, required: true },
    nombre:      { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup (props) {
    const attending      = ref('')
    const guests         = ref('')
    const guestsOptions  = ref([1, 2])
    const loading        = ref(true)
    const isSubmitting   = ref(false)
    const errorMessage   = ref('')

    // Identidad (oculta en UI, pero se envía)
    const guestFullName   = ref('')
    const identityLoading = ref(true)

    const cardRef = ref(null)

    // Trae nombre del invitado (nombre + apellidos)
    const fetchGuestIdentity = async () => {
      identityLoading.value = true
      try {
        const { data } = await axios.get(`${baseUrl}/api2/invitado/${props.idInvitado}`)
        const n = data?.nombre_invitado ?? ''
        const p = data?.apellidop_invitado ?? ''
        const m = data?.apellidom_invitado ?? ''
        const full = [n, p, m].map(s => (s || '').toString().trim()).filter(Boolean).join(' ')
        if (full) guestFullName.value = full
      } catch (e) {
        try {
          const url = `${baseUrl}/${props.nombre || ''}/${props.idFestejado || ''}/${props.idInvitado}`
          const { data: alt } = await axios.get(url)
          if (alt?.nombre_mostrar) guestFullName.value = String(alt.nombre_mostrar).trim()
        } catch (e2) {
          console.error('Error obteniendo identidad del invitado:', e2)
        }
      } finally {
        identityLoading.value = false
      }
    }

    // Trae límite de pases (num_pase)
    const fetchGuestLimit = async () => {
      loading.value = true
      try {
        const url = `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`
        const { data } = await axios.get(url)
        const numPases = Number(data?.num_pase ?? 0)
        guestsOptions.value = numPases > 0 ? Array.from({ length: numPases }, (_, i) => i + 1) : [1, 2]

        if (!guestFullName.value && data?.nombre_mostrar) {
          guestFullName.value = String(data.nombre_mostrar).trim()
        }
      } catch (err) {
        console.error('Error al obtener límite de invitados:', err)
        errorMessage.value = 'Error al cargar las opciones de invitados'
      } finally {
        loading.value = false
      }
    }

    const animateOnView = () => {
      const targets = cardRef.value?.querySelectorAll('.anim-el')
      if (!targets || !targets.length) return

      gsap.set(targets, { opacity: 0, y: 28 })

      const observer = new IntersectionObserver(async (entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const tl = gsap.timeline({ defaults: { duration: 0.65, ease: 'power2.out' } })
            tl.to(targets, { opacity: 1, y: 0, stagger: 0.12 })
            obs.unobserve(entry.target)
          }
        })
      }, { threshold: 0.35 })

      observer.observe(cardRef.value)
    }

    const handleSubmit = async () => {
      // Validaciones con SweetAlert
      if (!attending.value) {
        Swal.fire({
          icon: 'warning',
          title: 'Atención',
          text: 'Por favor selecciona si asistirás o no.',
          confirmButtonColor: '#65696c',
          allowOutsideClick: true
        })
        return
      }

      if (attending.value === 'si' && !guests.value) {
        await Swal.fire({
          icon: 'warning',
          title: 'Atención',
          text: 'Selecciona el número de asistentes.',
          confirmButtonColor: '#65696c',
          allowOutsideClick: true
        })
        // Enfocar el select después del alert
        await nextTick()
        const sel = document.getElementById('guests')
        if (sel) sel.focus()
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        // Decidir num_pases SOLO al confirmar
        const numPasesToSend = attending.value === 'si' ? Number(guests.value) : 0

        const formData = {
          nombre_asistencia: guestFullName.value || '',
          telefono_invitado: '',
          invitedby: 'novia',
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases: numPasesToSend
        }

        await axios.post(`${baseUrl}/asistencia`, formData, {
          headers: { 'Content-Type': 'application/json' }
        })

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: '¡Gracias por avisarnos!',
            text: 'Sentimos mucho que no puedas asistir.',
            confirmButtonColor: '#65696c',
          })
        } else {
          Swal.fire({
            icon: 'success',
            title: '¡Gracias!',
            text: 'Tu asistencia quedó confirmada.',
            confirmButtonColor: '#65696c',
          })
        }

        // Reset suave
        attending.value = ''
        guests.value = ''
      } catch (error) {
        console.error('Error confirmando asistencia:', error)
        errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.'
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(async () => {
      await Promise.all([fetchGuestIdentity(), fetchGuestLimit()])
      await nextTick()
      animateOnView()
    })

    return {
      attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage,
      guestFullName, identityLoading,
      handleSubmit, cardRef
    }
  }
}
</script>

<style scoped>
/* ===== Fondo / layout ===== */
.confirmation-container{
  width: 100%;
  min-height: 90vh;
  padding: 90px 20px;
  margin-top:90px;
  display:flex; align-items:center; justify-content:center;
  background:
    url('@/assets/F2.jpg') center/cover no-repeat;
  margin-top:0px;
}

/* ===== Tarjeta “glass” ===== */
.confirmation-card{
    margin-top:90px;

  width: min(680px, 96vw);
  border-radius: 24px;
  padding: 28px 22px;
}

/* ===== Encabezado ===== */
.form-header{ text-align:center; margin-bottom: 10px; }
.form-title{
  font-family: 'subtitulos', system-ui, sans-serif;
  letter-spacing: .5px;
  color:#b08a5a;
  line-height: 1.1;
  font-size: clamp(28px, 5vw, 36px);
  margin: 0;
}
.subtitulo{ font-weight: 600; font-size: clamp(22px, 4vw, 28px); font-family:'say2'; color:#b08a5a; }

/* ===== Grupos de formulario ===== */
.form-group{ margin: 14px 0; }
.form-label{
  font-size: 15px !important;
  display:block;
  margin-bottom:.5rem;
  font-family:'padres';
  font-weight:600;
  color:#3e3e3e;
}

/* ===== Radio group ===== */
.radio-group.column{ display:flex; flex-direction:column; gap:12px; }
.radio-option{
  display:flex; align-items:center; gap:10px;
  cursor:pointer;
}
.radio-input{ accent-color:#1f2937; width:18px; height:18px; }
.radio-label{ font-family:'Karla', sans-serif; color:#2d2d2d; }

/* Tiles suaves para inputs */
.soft-tile{
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 14px;
  padding: 12px 14px;
  transition: transform .18s ease, box-shadow .18s ease;
}
.soft-tile:hover{
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.08);
}

/* ===== Select asistentes ===== */
.select-wrap{ display:flex; }
.form-select{
  width: 100%;
  padding: 10px 12px;
  font-size: 1rem;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.08);
  background-color: rgba(255,255,255,0.9);
  outline: none;
  transition: box-shadow .2s ease, border-color .2s ease;
}
.form-select:focus{
  border-color: #2b2b2b;
  box-shadow: 0 0 0 4px rgba(31,41,55,.15);
}

/* ===== Botón centrado ===== */
.actions{
  margin-top: 16px;
  display:flex; justify-content:center; align-items:center;
}
.submit-button{
  min-width: 220px;
  background: #716355;
  color:#302e2e;
  font-weight: 700;
  letter-spacing:.2px;
  border-radius: 9999px;
  padding: 12px 28px;
  font-family:'padres', system-ui, sans-serif;
  border: none;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .2s ease, filter .2s ease;
  box-shadow: 0 10px 24px rgba(17,24,39,.25);
}
.submit-button:hover{ transform: translateY(-1px); filter: brightness(1.05); }
.submit-button:active{ transform: translateY(0); }
.submit-button:disabled{ opacity:.7; cursor:not-allowed; box-shadow:none; }

/* ===== Mensajes ===== */
.alert-danger{
  background-color:#f9e9ee;
  color:#3a2a30;
  border: 1px solid #f4d4de;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  text-align:center;
  box-shadow: 0 4px 10px rgba(184,0,76,0.08);
  margin-top: 14px;
}

.loading-indicator{ font-size:.9rem; color:#2b2b2b; margin-top:.35rem; }

/* ===== Responsive ===== */
@media (max-width: 576px){
  .confirmation-card{ padding: 18px 14px; }
}
</style>




<style scoped>
/* ===== Fondo / layout ===== */
.confirmation-container{
  width: 100%;
  min-height: 70vh;
  padding: 0px 20px;
  display:flex; align-items:center; justify-content:center;
  /* Fondo con imagen + velo sutil */
  background:
   
    url('@/assets/F2.jpg') center/cover no-repeat;
    margin-top:-80px;
}

/* ===== Tarjeta “glass” ===== */
.confirmation-card{
  width: min(680px, 96vw);
  border-radius: 24px;
  padding: 28px 22px;

}

/* ===== Encabezado ===== */
.form-header{ text-align:center; margin-bottom: 10px; }
.form-title{
  font-family: 'subtitulos', system-ui, sans-serif;
  letter-spacing: .5px;
  color:#b08a5a;
  line-height: 1.1;
  font-size: clamp(28px, 5vw, 36px);
  margin: 0;
}
.subtitulo{ font-weight: 600; font-size: clamp(22px, 4vw, 28px); font-family:'say2'; color:#b08a5a; }

/* ===== Grupos de formulario ===== */
.form-group{ margin: 14px 0; }
.form-label{
  font-size: 15px !important;
  display:block;
  margin-bottom:.5rem;
  font-family:'padres';
  font-weight:600;
  color:#3e3e3e;
}

/* ===== Radio group ===== */
.radio-group.column{ display:flex; flex-direction:column; gap:12px; }
.radio-option{
  display:flex; align-items:center; gap:10px;
  cursor:pointer;
}
.radio-input{ accent-color:#1f2937; width:18px; height:18px; }
.radio-label{ font-family:'Karla', sans-serif; color:#2d2d2d; }

/* Tiles suaves para inputs */
.soft-tile{
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 14px;
  padding: 12px 14px;
  transition: transform .18s ease, box-shadow .18s ease;
}
.soft-tile:hover{
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.08);
}

/* ===== Select asistentes ===== */
.select-wrap{ display:flex; }
.form-select{
  width: 100%;
  padding: 10px 12px;
  font-size: 1rem;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.08);
  background-color: rgba(255,255,255,0.9);
  outline: none;
  transition: box-shadow .2s ease, border-color .2s ease;
}
.form-select:focus{
  border-color: #2b2b2b;
  box-shadow: 0 0 0 4px rgba(31,41,55,.15);
}

/* ===== Botón centrado ===== */
.actions{
  margin-top: 16px;
  display:flex; justify-content:center; align-items:center;
}
.submit-button{
  min-width: 220px;
  background: #645e4f;
  color:#ffffff;
  font-weight: 700;
  letter-spacing:.2px;
  border-radius: 9999px;
  padding: 12px 28px;
  font-family:'padres', system-ui, sans-serif;
  border: none;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .2s ease, filter .2s ease;
  box-shadow: 0 10px 24px rgba(17,24,39,.25);
}
.submit-button:hover{ transform: translateY(-1px); filter: brightness(1.05); }
.submit-button:active{ transform: translateY(0); }
.submit-button:disabled{ opacity:.7; cursor:not-allowed; box-shadow:none; }

/* ===== Mensajes ===== */
.alert-danger{
  background-color:#f9e9ee;
  color:#3a2a30;
  border: 1px solid #f4d4de;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  text-align:center;
  box-shadow: 0 4px 10px rgba(184,0,76,0.08);
  margin-top: 14px;
}

.loading-indicator{ font-size:.9rem; color:#2b2b2b; margin-top:.35rem; }

/* ===== Responsive ===== */
@media (max-width: 576px){
  .confirmation-card{ padding: 18px 14px; }
}
</style>
